import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/hooks/use-toast';
import { getUsers, updateUserRoles } from '@/lib/api-calls';
import { QueryKeys } from '@/lib/queries';
import { airAdmin, isAirAdmin, isAirAdminRole } from '@airreading/common';
import { UserRoleAssignmentOptions, UserWithRolesAndProfiles } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useState } from 'react';

export interface ManageAirAdminProps {
  trigger: ReactNode;
  user: UserWithRolesAndProfiles;
}

export const ManageAirAdmin = ({ trigger, user }: ManageAirAdminProps) => {
  const [open, setOpen] = useState(false);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateUserRoles,
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const userIsAdminNow = isAirAdmin(user);

  const onSubmit = () => {
    const updatedRoles: UserRoleAssignmentOptions = userIsAdminNow
      ? user.userRoles.filter((role) => !isAirAdminRole(role))
      : [...user.userRoles, airAdmin()];

    return mutateAsync({
      userId: user.id,
      roles: updatedRoles.map(({ role, schoolId, status }) => ({ role, schoolId, status })),
    })
      .then(() => {
        setOpen(false);
        queryClient.invalidateQueries(getUsers());
        queryClient.invalidateQueries({ queryKey: QueryKeys.getUser(user.id) });
        const fullName = user.fullNameOrEmail;
        toast({
          title: `${userIsAdminNow ? 'Air admin removed from ' : 'Air admin granted to '}${fullName}`,
        });
      })
      .catch(() => {
        toast({
          title: 'Air admin update failed',
        });
      });
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{userIsAdminNow ? 'Revoke Air Admin' : 'Promote as Air Admin'}</AlertDialogTitle>
          <AlertDialogDescription>
            {userIsAdminNow
              ? `Remove ${user.fullNameOrEmail} from being an Air Admin.`
              : `Add ${user.fullNameOrEmail} as an Air Admin.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button
              loading={isPending}
              onClick={(e) => {
                e.preventDefault();
                onSubmit();
              }}
            >
              {userIsAdminNow ? 'Revoke Air Admin' : 'Promote as Air Admin'}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
